/**
 * Generates a black-and-white mask from an image using GPU-accelerated
 * webgl-filters pipeline.
 *
 * Algorithm:
 * 1. Place white background under transparent pixels (alphaUnder).
 * 2. Invert colors.
 * 3. Threshold to binary B&W (proper luminance weighting).
 * 4. Dilate × 2 passes to fill gaps.
 * 5. Erode × 2 passes to restore edges.
 *
 * Returns a data URL of the resulting mask.
 */

import {
  glFilters,
  alphaUnder,
  invert,
  threshold,
  dilate,
  erode,
} from "webgl-filters";

export async function generateMaskFromImage(imageUrl: string): Promise<string> {
  // Step 1: White bg under transparency + invert + threshold to B&W
  let current = await glFilters()
    .addFilter(alphaUnder({ r: 255, g: 255, b: 255 }))
    .addFilter(invert())
    .addFilter(threshold({ cutoff: 0.005 }))
    .applyAsync(imageUrl);

  // Step 2: Dilate (grow) — 2 passes
  for (let i = 0; i < 2; i++) {
    current = glFilters().addFilter(dilate()).apply(current);
  }

  // Step 3: Erode (shrink) — 2 passes
  for (let i = 0; i < 2; i++) {
    current = glFilters().addFilter(erode()).apply(current);
  }

  // Write result to canvas and return data URL
  const resultCanvas = document.createElement("canvas");
  resultCanvas.width = current.width;
  resultCanvas.height = current.height;
  const rctx = resultCanvas.getContext("2d")!;
  const out = new ImageData(
    new Uint8ClampedArray(current.data),
    current.width,
    current.height
  );
  rctx.putImageData(out, 0, 0);
  return resultCanvas.toDataURL("image/png");
}
